// front/src/preview-mode/graph/site.ts
import type {
  EntryPoint,
  Location,
  PageEdge,
  PageGraph,
  PageNode,
  Provider,
  ServiceLine,
  ServiceLineKey,
} from "./types";

export const SERVICE_LINES: ServiceLine[] = [
  { key: "cardiology", name: "Cardiology", slug: "cardiology" },
  { key: "oncology", name: "Oncology", slug: "oncology" },
  { key: "orthopedics", name: "Orthopedics", slug: "orthopedics" },
  {
    key: "maternity",
    name: "Maternity & OB/GYN",
    slug: "maternity-obgyn",
  },
  { key: "primary_care", name: "Primary Care", slug: "primary-care" },
];

export const LOCATIONS: Location[] = [
  {
    id: "loc-chi-north",
    name: "Meridian Chicago North",
    slug: "chicago-north",
    city: "Chicago",
    state: "IL",
    serviceLines: ["cardiology", "oncology", "orthopedics", "primary_care"],
  },
  {
    id: "loc-chi-west",
    name: "Meridian Chicago West",
    slug: "chicago-west",
    city: "Chicago",
    state: "IL",
    serviceLines: ["maternity", "primary_care", "orthopedics"],
  },
  {
    id: "loc-naperville",
    name: "Meridian Naperville",
    slug: "naperville",
    city: "Naperville",
    state: "IL",
    serviceLines: ["primary_care", "maternity"],
  },
  {
    id: "loc-milwaukee",
    name: "Meridian Milwaukee Central",
    slug: "milwaukee-central",
    city: "Milwaukee",
    state: "WI",
    serviceLines: ["cardiology", "orthopedics", "primary_care"],
  },
  {
    id: "loc-madison",
    name: "Meridian Madison",
    slug: "madison",
    city: "Madison",
    state: "WI",
    serviceLines: ["oncology", "primary_care", "maternity"],
  },
  {
    id: "loc-indy-downtown",
    name: "Meridian Indianapolis Downtown",
    slug: "indianapolis-downtown",
    city: "Indianapolis",
    state: "IN",
    serviceLines: ["cardiology", "oncology", "orthopedics", "primary_care"],
  },
  {
    id: "loc-indy-north",
    name: "Meridian Indianapolis North",
    slug: "indianapolis-north",
    city: "Indianapolis",
    state: "IN",
    serviceLines: ["maternity", "primary_care"],
  },
  {
    id: "loc-fort-wayne",
    name: "Meridian Fort Wayne",
    slug: "fort-wayne",
    city: "Fort Wayne",
    state: "IN",
    serviceLines: ["primary_care", "orthopedics"],
  },
];

// 25 providers distributed across locations and service lines.
// IDs are stable strings, not UUIDs — providers aren't a Freshpaint entity type.
const PROVIDER_NAMES: Array<[string, string, ServiceLineKey]> = [
  ["Dr. Anita Shah", "MD, Cardiologist", "cardiology"],
  ["Dr. Marcus Reeves", "MD, Cardiologist", "cardiology"],
  ["Dr. Helena Park", "MD, Cardiologist", "cardiology"],
  ["Dr. David Okafor", "MD, Cardiologist", "cardiology"],
  ["Dr. Priya Menon", "MD, Oncologist", "oncology"],
  ["Dr. Liam Fitzgerald", "MD, Oncologist", "oncology"],
  ["Dr. Jasmine Cho", "MD, Oncologist", "oncology"],
  ["Dr. Ravi Das", "MD, Oncologist", "oncology"],
  ["Dr. Sofia Esposito", "MD, Orthopedic Surgeon", "orthopedics"],
  ["Dr. Nathan Brewer", "MD, Orthopedic Surgeon", "orthopedics"],
  ["Dr. Yara Khalil", "MD, Orthopedic Surgeon", "orthopedics"],
  ["Dr. Trevor Delacroix", "MD, Orthopedic Surgeon", "orthopedics"],
  ["Dr. Grace Nguyen", "MD, OB/GYN", "maternity"],
  ["Dr. Mira Lindqvist", "MD, OB/GYN", "maternity"],
  ["Dr. Rosa Beltran", "MD, OB/GYN", "maternity"],
  ["Dr. Aisha Rahman", "MD, OB/GYN", "maternity"],
  ["Dr. Benjamin Carr", "MD, Family Medicine", "primary_care"],
  ["Dr. Emma Sørensen", "MD, Family Medicine", "primary_care"],
  ["Dr. Theo Martens", "MD, Family Medicine", "primary_care"],
  ["Dr. Tasha Calhoun", "MD, Internal Medicine", "primary_care"],
  ["Dr. Ivan Petrov", "MD, Internal Medicine", "primary_care"],
  ["Dr. Clara Okonkwo", "MD, Pediatrics", "primary_care"],
  ["Dr. Quentin Marsh", "MD, Pediatrics", "primary_care"],
  ["Dr. Naomi Becker", "MD, Family Medicine", "primary_care"],
  ["Dr. Joon Park", "MD, Internal Medicine", "primary_care"],
];

// Round-robin assign providers to locations that support their service line.
export const PROVIDERS: Provider[] = (() => {
  const result: Provider[] = [];
  const perLineIndex: Record<ServiceLineKey, number> = {
    cardiology: 0,
    oncology: 0,
    orthopedics: 0,
    maternity: 0,
    primary_care: 0,
  };
  for (const [name, title, line] of PROVIDER_NAMES) {
    const eligibleLocations = LOCATIONS.filter((l) =>
      l.serviceLines.includes(line),
    );
    const loc =
      eligibleLocations[perLineIndex[line] % eligibleLocations.length];
    perLineIndex[line]++;
    const slug = name
      .toLowerCase()
      .replace(/^dr\.\s*/, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    result.push({
      id: `provider-${slug}`,
      name,
      title,
      slug,
      locationId: loc.id,
      serviceLine: line,
    });
  }
  return result;
})();

export function buildPageGraph(): PageGraph {
  const nodes: PageNode[] = [
    { path: "/", kind: "home" },
    { path: "/services", kind: "service_index" },
    ...SERVICE_LINES.map(
      (s): PageNode => ({
        path: `/services/${s.slug}`,
        kind: "service_detail",
      }),
    ),
    { path: "/locations", kind: "location_index" },
    ...LOCATIONS.map(
      (l): PageNode => ({
        path: `/locations/${l.slug}`,
        kind: "location_detail",
      }),
    ),
    { path: "/providers", kind: "provider_index" },
    ...PROVIDERS.map(
      (p): PageNode => ({
        path: `/providers/${p.slug}`,
        kind: "provider_detail",
      }),
    ),
    { path: "/appointments", kind: "appointments" },
    { path: "/appointments/new", kind: "appointments_new" },
    { path: "/appointments/new/confirmation", kind: "appointments_new" },
    { path: "/insurance", kind: "insurance" },
    { path: "/patient-portal", kind: "patient_portal" },
    { path: "/about", kind: "about" },
    { path: "/contact", kind: "contact" },
  ];

  const edges: PageEdge[] = [];

  // Home → service index / service details / locations / appointments
  edges.push({ from: "/", to: "/services", weight: 25 });
  edges.push({ from: "/", to: "/locations", weight: 10 });
  edges.push({ from: "/", to: "/appointments", weight: 15 });
  edges.push({ from: "/", to: "/insurance", weight: 5 });
  edges.push({ from: "/", to: "/providers", weight: 5 });
  for (const s of SERVICE_LINES) {
    edges.push({ from: "/", to: `/services/${s.slug}`, weight: 8 });
  }

  // Service index → service details
  for (const s of SERVICE_LINES) {
    edges.push({ from: "/services", to: `/services/${s.slug}`, weight: 20 });
  }

  // Service detail → appointments / providers / locations
  for (const s of SERVICE_LINES) {
    edges.push({
      from: `/services/${s.slug}`,
      to: "/appointments",
      weight: 30,
    });
    edges.push({
      from: `/services/${s.slug}`,
      to: "/providers",
      weight: 15,
    });
    edges.push({
      from: `/services/${s.slug}`,
      to: "/locations",
      weight: 10,
    });
    edges.push({ from: `/services/${s.slug}`, to: "/insurance", weight: 8 });
  }

  // Location index → location detail
  for (const l of LOCATIONS) {
    edges.push({ from: "/locations", to: `/locations/${l.slug}`, weight: 12 });
  }

  // Location detail → service detail + appointments
  for (const l of LOCATIONS) {
    for (const lineKey of l.serviceLines) {
      const line = SERVICE_LINES.find((s) => s.key === lineKey);
      if (!line) continue;
      edges.push({
        from: `/locations/${l.slug}`,
        to: `/services/${line.slug}`,
        weight: 8,
      });
    }
    edges.push({
      from: `/locations/${l.slug}`,
      to: "/appointments",
      weight: 15,
    });
  }

  // Provider index / detail
  for (const p of PROVIDERS) {
    edges.push({ from: "/providers", to: `/providers/${p.slug}`, weight: 4 });
    edges.push({
      from: `/providers/${p.slug}`,
      to: "/appointments",
      weight: 20,
    });
  }

  // Appointments → appointments/new (booking flow entry)
  edges.push({
    from: "/appointments",
    to: "/appointments/new",
    weight: 40,
    events: ["appointment_booking_started"],
  });
  // Insurance → form_started
  edges.push({
    from: "/insurance",
    to: "/appointments",
    weight: 15,
  });

  // Footer/secondary pages — reachable from home and contact-back paths
  edges.push({ from: "/", to: "/about", weight: 2 });
  edges.push({ from: "/", to: "/contact", weight: 3 });
  edges.push({ from: "/", to: "/patient-portal", weight: 6 });
  edges.push({ from: "/about", to: "/services", weight: 8 });
  edges.push({ from: "/about", to: "/locations", weight: 5 });
  edges.push({ from: "/contact", to: "/appointments", weight: 12 });
  edges.push({ from: "/contact", to: "/locations", weight: 5 });
  edges.push({ from: "/patient-portal", to: "/appointments", weight: 6 });

  return {
    nodes,
    edges,
    entryPoints: [
      { path: "/", channelWeights: { organic: 40, direct: 30, email: 10 } },
      {
        path: "/services/cardiology",
        channelWeights: { paid: 15, social: 5 },
      },
      {
        path: "/services/oncology",
        channelWeights: { paid: 12, organic: 5 },
      },
      {
        path: "/services/orthopedics",
        channelWeights: { paid: 20, social: 6 },
      },
      {
        path: "/services/maternity-obgyn",
        channelWeights: { paid: 14, social: 8 },
      },
      {
        path: "/services/primary-care",
        channelWeights: { paid: 10, organic: 10 },
      },
      {
        path: "/locations/chicago-north",
        channelWeights: { organic: 4, direct: 5 },
      },
      {
        path: "/locations/milwaukee-central",
        channelWeights: { organic: 3, direct: 4 },
      },
      {
        path: "/appointments",
        channelWeights: { direct: 5, email: 8 },
      },
    ] as EntryPoint[],
  };
}
